/**
 * DashboardPage — TREND view (Phase 7D pivot).
 *
 * Before Phase 7 this was a card grid of "right now" metrics: live track
 * count, current alerts, mission status, sensor health. All of which
 * duplicate what /map's StatusRail and RightRail already show. Operators
 * had two surfaces saying the same thing.
 *
 * After Phase 7D this page is the analytical surface for the platform —
 * answers "what happened over the last 24h / 7d / 30d?" The map is the
 * operational "right now" surface; this page is the "in retrospect"
 * surface. Distinct surfaces, distinct jobs, no overlap.
 *
 * Layout:
 *   1. Header with time-range selector (24h / 7d / 30d).
 *   2. KPI strip: total alerts, alerts/day, days with activity, peak day.
 *   3. Big spatial heatmap (lifted from analytics) — the dominant visual.
 *   4. Alerts-by-date bar chart.
 *   5. Top drones + top serials (with repeat-offender coloring from 5C).
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ChartBarSquareIcon,
  ExclamationTriangleIcon,
  RocketLaunchIcon,
  IdentificationIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { reportsApi } from '../services/reportsApi';
import { intelligenceApi } from '../services/analyticsService';
import { useAuthStore } from '../store/authStore';
import { SkeletonCard, SkeletonChart } from '../components/common/Skeleton';
import AlertsByDateChart from '../components/reports/AlertsByDateChart';
import RankedListCard from '../components/reports/RankedListCard';
import SpatialHeatmapMap from '../components/analytics/SpatialHeatmapMap';

type RangeKey = '24h' | '7d' | '30d';

const RANGES: { key: RangeKey; label: string; hours: number }[] = [
  { key: '24h', label: 'Last 24 hours', hours: 24 },
  { key: '7d', label: 'Last 7 days', hours: 24 * 7 },
  { key: '30d', label: 'Last 30 days', hours: 24 * 30 },
];

function rangeBounds(hours: number): { from: Date; to: Date } {
  const to = new Date();
  const from = new Date(to.getTime() - hours * 3600_000);
  return { from, to };
}

export default function DashboardPage() {
  const [rangeKey, setRangeKey] = useState<RangeKey>('7d');
  const range = useMemo(() => {
    const r = RANGES.find((x) => x.key === rangeKey)!;
    return { ...rangeBounds(r.hours), label: r.label, days: r.hours / 24 };
  }, [rangeKey]);

  const { user } = useAuthStore();

  const summaryQuery = useQuery({
    queryKey: ['dashboard', 'summary', rangeKey],
    queryFn: () =>
      reportsApi.getSummary({
        from: range.from.toISOString(),
        to: range.to.toISOString(),
      }),
    staleTime: 60 * 1000,
  });

  const heatmapQuery = useQuery({
    queryKey: ['dashboard', 'heatmap', rangeKey, user?.tenantId],
    queryFn: () =>
      intelligenceApi.getHeatmap(
        user!.tenantId,
        range.from.toISOString(),
        range.to.toISOString(),
      ),
    enabled: !!user?.tenantId,
    staleTime: 5 * 60 * 1000,
    retry: false, // analytics service may be offline; fail fast and show fallback
  });

  return (
    <div className="min-h-screen bg-iron-900 p-6">
      {/* ---- Header ---- */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="page-title">Trend Overview</h1>
          <p className="page-subtitle">
            {range.label} ·{' '}
            {summaryQuery.data
              ? `${summaryQuery.data.kpis.totalAlerts.toLocaleString()} detections`
              : 'Loading…'}
          </p>
        </div>

        {/* Range selector — segmented control. Reads as a unit; less
            visual noise than three separate buttons. */}
        <div className="inline-flex bg-iron-800 border border-iron-600 rounded-md p-0.5">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => setRangeKey(r.key)}
              className={clsx(
                'px-3 py-1.5 text-[11px] font-mono font-semibold uppercase tracking-[0.15em] rounded-sm transition-colors',
                rangeKey === r.key
                  ? 'bg-accent text-iron-900'
                  : 'text-iron-300 hover:text-iron-100',
              )}
            >
              {r.key}
            </button>
          ))}
        </div>
      </div>

      {summaryQuery.isError && (
        <div className="mb-4 flex items-center gap-2 px-4 py-3 rounded-md bg-alert/10 border border-alert/30 text-alert text-sm">
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
          <span>
            Failed to load trends: {(summaryQuery.error as Error)?.message || 'Unknown error'}
          </span>
        </div>
      )}

      {/* ---- KPI strip ---- */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {summaryQuery.isLoading || !summaryQuery.data ? (
          <>
            <SkeletonCard /><SkeletonCard /><SkeletonCard /><SkeletonCard />
          </>
        ) : (
          <>
            <KpiCard
              label="Total Detections"
              value={summaryQuery.data.kpis.totalAlerts.toLocaleString()}
              sublabel={`in ${Math.round(range.days)} day${range.days === 1 ? '' : 's'}`}
            />
            <KpiCard
              label="Mean / Day"
              value={summaryQuery.data.kpis.alertsPerDay.toFixed(1)}
              sublabel="averaged across the range"
            />
            <KpiCard
              label="Days w/ Activity"
              value={summaryQuery.data.kpis.daysWithAlerts.toString()}
              sublabel={`of ${Math.max(1, Math.round(range.days))}`}
            />
            <KpiCard
              label="Peak Day"
              value={summaryQuery.data.kpis.maxAlertsPerDay.toLocaleString()}
              sublabel="detections on the busiest day"
            />
          </>
        )}
      </div>

      {/* ---- Spatial heatmap — dominant visual ---- */}
      <div className="glass-card p-5 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <ChartBarSquareIcon className="h-5 w-5 text-accent" />
          <h2 className="text-sm font-semibold text-iron-100">Spatial Activity</h2>
        </div>
        {heatmapQuery.isLoading ? (
          <SkeletonChart />
        ) : heatmapQuery.isError || !heatmapQuery.data ? (
          <div className="text-sm text-iron-400 py-12 text-center">
            Heatmap data unavailable — analytics service may be offline.
          </div>
        ) : (
          <SpatialHeatmapMap heatmap={heatmapQuery.data} height={380} />
        )}
      </div>

      {/* ---- Alerts by date ---- */}
      <div className="mb-6">
        {summaryQuery.isLoading || !summaryQuery.data ? (
          <SkeletonChart />
        ) : (
          <AlertsByDateChart data={summaryQuery.data.alertsByDate} />
        )}
      </div>

      {/* ---- Top drones + serials ---- */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {summaryQuery.isLoading || !summaryQuery.data ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : (
          <>
            <RankedListCard
              title="Top Drones"
              icon={RocketLaunchIcon}
              rows={summaryQuery.data.topDrones.map((d) => ({
                label: d.model || 'Unknown',
                sublabel: d.manufacturer ?? undefined,
                count: d.count,
                repeatCount: d.repeatCount,
              }))}
            />
            <RankedListCard
              title="Top Serials"
              icon={IdentificationIcon}
              barColor="bg-sky/70"
              rows={summaryQuery.data.topSerials.map((s) => ({
                label: s.serialNumber,
                count: s.count,
                repeatCount: s.repeatCount,
              }))}
            />
          </>
        )}
      </div>
    </div>
  );
}

// ---------- KPI card ----------

function KpiCard({
  label,
  value,
  sublabel,
}: {
  label: string;
  value: string;
  sublabel?: string;
}) {
  return (
    <div className="glass-card p-4">
      <div className="text-[10px] font-mono font-semibold uppercase tracking-[0.2em] text-iron-400">
        {label}
      </div>
      <div className="mt-2 text-2xl font-semibold tabular-nums text-iron-100">
        {value}
      </div>
      {sublabel && (
        <div className="mt-1 text-[11px] text-iron-400">{sublabel}</div>
      )}
    </div>
  );
}
